import { useEffect } from 'react';
import { useGetApiV1ThemeActive } from '@/shared/api/generated/theme/theme';
import { applyBundledTheme, applyTheme } from '@/shared/config/theme/apply-theme';
import { useThemeMode } from '@/shared/config/theme/theme-mode-context';
import type { ThemeTokens } from '@/shared/config/theme/default-theme';
import { rolesIn, SystemRoles } from '@/features/auth/model/use-roles';
import { useSession } from '@/features/auth/model/use-session';
import { useSessionRenewal } from '@/features/auth/model/use-session-renewal';
import { SignInForm } from '@/features/auth/ui/SignInForm';
import { useTopic } from '@/shared/lib/mqtt/use-topic';
import { clearSession } from '@/shared/api/session';
import { RouterProvider } from '@tanstack/react-router';
import { router } from '@/app/router';

export function App() {
  const { session } = useSession();

  // ⚠️ Before the token expires, not after something fails — a control room is watched rather than
  // clicked, and an idle panel never provokes the 401 the interceptor refreshes on.
  useSessionRenewal();

  // §7: the active theme is public and fetched on boot. The bundled default has already
  // painted (see main.tsx), so this replaces it rather than gating the first frame.
  const theme = useGetApiV1ThemeActive();

  // Daylight or dark. Listed as a dependency below so switching repaints immediately rather than
  // at the next theme fetch.
  const { resolved } = useThemeMode();

  useEffect(() => {
    if (!theme.data?.tokensJson) {
      // ⚠️ The bundled theme is re-applied on a mode change even before the API answers. Without
      // this the panel would stay in the palette it booted with until the fetch landed — and on a
      // cold start with no network, forever.
      applyBundledTheme(resolved);
      return;
    }

    try {
      // The API hands over the token document as a JSON STRING, so it is parsed here.
      // Wrapped, because a malformed document must leave the bundled palette in place —
      // a control room with no colours is worse than one running a version behind.
      applyTheme(JSON.parse(theme.data.tokensJson) as ThemeTokens, resolved);
    } catch {
      // Intentionally silent for the operator; the bundled theme is already applied.
    }
  }, [theme.data?.tokensJson, resolved]);

  // ⚠️ §7's whole point: a theme activated in the dashboard reaches every open panel WITHOUT a
  // rebuild and without waiting for the operator's next cold start. The message carries only the
  // version — the tokens come from the API, so one document stays the source of truth and a
  // tampered broker message cannot repaint a control room.
  //
  // Retained (§8), so a panel opened a minute after the change still learns about it.
  useTopic('system/theme', () => {
    void theme.refetch();
  });

  // ⚠️ **A session that is not an administrator's is dropped rather than let in.**
  //
  // The association's decision is that the dashboard is theirs and everyone else — the committee,
  // desk control, the head of control — works from the app. `SignInForm` turns them away at the
  // door; this is for a session stored BEFORE that door existed, which would otherwise walk
  // straight past it into the ten screens that all answer «لا تملك صلاحية هذا الإجراء».
  //
  // ⚠️ Cleared, not merely ignored: leaving it would put this branch between the operator and the
  // sign-in form on every load, with nothing they could do about it.
  //
  // ⚠️ An affordance, never a boundary — see `rolesIn`. The token is unverified here, and getting
  // past this wins nothing: the server authorises every request by policy (§12.2).
  if (session && !rolesIn(session.accessToken).includes(SystemRoles.SystemAdministrator)) {
    clearSession();
    return <SignInForm />;
  }

  if (!session) {
    // ⚠️ The router is not mounted for a signed-out operator, deliberately.
    //
    // Rendering it and guarding each route would put the shell — its navigation, its page titles — on
    // screen behind a sign-in form, and every route's loader would fire and 401. The API enforces
    // authorisation regardless; this is about not showing a control-room chrome to someone who has no
    // session.
    return <SignInForm />;
  }

  return <RouterProvider router={router} />;
}
